import React, { useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronUp, RotateCcw, Zap, Fuel, TrendingDown, TrendingUp, PanelRightClose, PanelRightOpen } from 'lucide-react';
import { Link } from 'react-router-dom';
import VehicleCard from '../../components/basic_calculator/VehicleCard';
import TCOResults from '../../components/basic_calculator/TCOResults';
import IncentiveCalculator from '../../components/basic_calculator/IncentiveCalculator';
import ChargerOptions from '../../data/charger_options.json';
import BasicVehicleCatalog from '../../data/basic_vehicle_catalog.json';
import { trackMarketingFunnel } from '../../utils/marketingAnalytics';

const CLASS_KEYS = ['LightDuty', 'MediumDuty', 'HeavyDuty'];
const VEHICLE_CLASS_OPTIONS = [
    { value: 'All', label: 'All Classes' },
    { value: 'Light Duty', label: 'Light Duty' },
    { value: 'Medium Duty', label: 'Medium Duty' },
    { value: 'Heavy Duty', label: 'Heavy Duty' },
];
const DEFAULT_CLASS_FILTER = 'Heavy Duty';

function flattenCatalog(catalog, powertrain) {
    return CLASS_KEYS.flatMap(classKey => (catalog?.[classKey] || []).map(item => ({
        ...item,
        powertrain,
    })));
}

function filterEntriesByClass(entries, vehicleClass) {
    if (vehicleClass === 'All') return entries;
    return entries.filter(entry => entry.vehicleClass === vehicleClass);
}

function getOneStandardPerClass(entries) {
    const byClass = {
        'Light Duty': [],
        'Medium Duty': [],
        'Heavy Duty': [],
    };

    entries.forEach((entry) => {
        if (byClass[entry.vehicleClass]) byClass[entry.vehicleClass].push(entry);
    });

    return Object.values(byClass)
        .map((classEntries) => {
            if (!classEntries.length) return null;
            const dieselDefault = classEntries.find(entry => entry.powertrain === 'diesel' && entry.isDefaultOption);
            if (dieselDefault) return dieselDefault;
            const anyDefault = classEntries.find(entry => entry.isDefaultOption);
            return anyDefault || classEntries[0];
        })
        .filter(Boolean);
}

function formatEvModelLabel(entry) {
    const rangeValue = Number(entry.operationRange);
    if (Number.isFinite(rangeValue) && rangeValue > 0) {
        return `${entry.model} (${Math.round(rangeValue)} mi)`;
    }
    return `${entry.model} (Range N/A)`;
}

function buildSelectionIndex(entries, modelLabelFormatter = (entry) => entry.model) {
    const grouped = {};
    const selectionById = {};
    const entryBySelection = {};

    entries.forEach((entry) => {
        const make = entry.make;
        const modelLabel = modelLabelFormatter(entry);
        if (!grouped[make]) grouped[make] = { models: [] };
        if (!grouped[make].models.includes(modelLabel)) grouped[make].models.push(modelLabel);
        selectionById[entry.id] = { make, model: modelLabel };
        entryBySelection[`${make}::${modelLabel}`] = entry;
    });

    const sortedData = Object.fromEntries(
        Object.keys(grouped)
            .sort((a, b) => a.localeCompare(b))
            .map(make => [make, { models: grouped[make].models.sort((a, b) => a.localeCompare(b)) }]),
    );

    return { data: sortedData, selectionById, entryBySelection };
}

function getEntryFromIndex(index, make, model) {
    return index.entryBySelection[`${make}::${model}`] || null;
}

function getDefaultSelection(entries, index) {
    const defaultEntry = entries.find(entry => entry.isDefaultOption) || entries[0];
    if (!defaultEntry) return { make: '', model: '' };
    return index.selectionById[defaultEntry.id] || { make: '', model: '' };
}

const EV_ENTRIES = flattenCatalog(BasicVehicleCatalog.ev, 'ev');
const DIESEL_ENTRIES = flattenCatalog(BasicVehicleCatalog.diesel, 'diesel');
const GAS_ENTRIES = flattenCatalog(BasicVehicleCatalog.gas, 'gas');
const ICE_ENTRIES = [...DIESEL_ENTRIES, ...GAS_ENTRIES];
const ICE_STANDARD_ENTRIES = getOneStandardPerClass(ICE_ENTRIES);

const DEFAULT_EV_FILTERED_ENTRIES = filterEntriesByClass(EV_ENTRIES, DEFAULT_CLASS_FILTER);
const DEFAULT_ICE_FILTERED_ENTRIES = filterEntriesByClass(ICE_STANDARD_ENTRIES, DEFAULT_CLASS_FILTER);

const DEFAULT_EV_INDEX = buildSelectionIndex(DEFAULT_EV_FILTERED_ENTRIES, formatEvModelLabel);
const DEFAULT_ICE_INDEX = buildSelectionIndex(DEFAULT_ICE_FILTERED_ENTRIES);

const DEFAULT_EV_SELECTION = getDefaultSelection(DEFAULT_EV_FILTERED_ENTRIES, DEFAULT_EV_INDEX);
const DEFAULT_ICE_SELECTION = getDefaultSelection(DEFAULT_ICE_FILTERED_ENTRIES, DEFAULT_ICE_INDEX);

const DEFAULT_EV_ENTRY = getEntryFromIndex(DEFAULT_EV_INDEX, DEFAULT_EV_SELECTION.make, DEFAULT_EV_SELECTION.model);
const DEFAULT_ICE_ENTRY = getEntryFromIndex(DEFAULT_ICE_INDEX, DEFAULT_ICE_SELECTION.make, DEFAULT_ICE_SELECTION.model);

/* ── Default parameter sets ── */

const DEFAULT_EV_FIN = {
    msrp: DEFAULT_EV_ENTRY?.price || 318000, incentives: DEFAULT_EV_ENTRY?.incentives || 120000, taxRate: 9.5,
    downPaymentPct: 20, loanPeriod: 5, interestRate: 8.5,
    holdingPeriod: 12, residualValue: 15, insurance: 15000, registrationFee: 4800,
};
const DEFAULT_DIESEL_FIN = {
    msrp: DEFAULT_ICE_ENTRY?.price || 140000, incentives: 0, taxRate: 9.5,
    downPaymentPct: 20, loanPeriod: 5, interestRate: 8.5,
    holdingPeriod: 12, residualValue: 25, insurance: 15000, registrationFee: 4800,
};
const DEFAULT_EV_OPS = {
    annualMileage: 60000, maintenanceCost: 0.1025, tireCost: 0.07,
    energyEfficiency: DEFAULT_EV_ENTRY?.energyEfficiency || 2, electricityCost: 0.30,
    chargerCost: 75000, chargerIncentives: 10000, demandCharge: 20, peakLoad: 50,
    thirdPartyFee: 0.50,
};
const DEFAULT_DIESEL_OPS = {
    annualMileage: 60000, maintenanceCost: 0.21, tireCost: 0.05,
    fuelEfficiency: DEFAULT_ICE_ENTRY?.fuelEconomy || 6.5, fuelCost: 4.50,
};

/* ── TCO calculation ── */

export function calculateTCO(fin, ops, isEV, ownChargers = true) {
    const netWithTax = (fin.msrp * (1 + fin.taxRate / 100)) - fin.incentives;
    const loanAmount = netWithTax * (1 - fin.downPaymentPct / 100);
    const monthlyRate = fin.interestRate / 100 / 12;
    const n = fin.loanPeriod * 12;
    const monthly = monthlyRate > 0
        ? loanAmount * (monthlyRate * Math.pow(1 + monthlyRate, n)) / (Math.pow(1 + monthlyRate, n) - 1)
        : loanAmount / n;
    const financingCost = Math.round(monthly * n - loanAmount);
    const residualRecovery = fin.msrp * (fin.residualValue / 100);
    const vehicleCost = Math.round(Math.max(0, netWithTax - residualRecovery));
    const totalMiles = ops.annualMileage * fin.holdingPeriod;
    const maintenance = Math.round(totalMiles * ops.maintenanceCost);
    const tires = Math.round(totalMiles * ops.tireCost);
    const insurance = Math.round(fin.insurance * fin.holdingPeriod);
    const registration = Math.round(fin.registrationFee * fin.holdingPeriod);

    let energy = 0;
    if (isEV) {
        if (ownChargers) {
            energy = Math.round(
                totalMiles * ops.energyEfficiency * ops.electricityCost +
                Math.max(0, ops.chargerCost - ops.chargerIncentives) +
                ops.demandCharge * ops.peakLoad * 12 * fin.holdingPeriod,
            );
        } else {
            energy = Math.round(ops.thirdPartyFee * ops.energyEfficiency * totalMiles);
        }
    } else {
        energy = Math.round((totalMiles / ops.fuelEfficiency) * ops.fuelCost);
    }

    const total = vehicleCost + financingCost + maintenance + tires + insurance + registration + energy;
    return { vehicleCost, financingCost, maintenance, tires, insurance, registration, energy, total };
}

/* ── Tiny UI primitives ── */

function ParamField({ label, value, onChange, prefix, suffix, step = 'any', labelRight }) {
    return (
        <div>
            <div className="flex items-center justify-between mb-1">
                <label className="text-xs text-gray-400">{label}</label>
                {labelRight}
            </div>
            <div className="relative">
                {prefix && (
                    <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-gray-400 pointer-events-none">
                        {prefix}
                    </span>
                )}
                <input
                    type="number"
                    value={value}
                    onChange={e => onChange(parseFloat(e.target.value) || 0)}
                    step={step}
                    min={0}
                    className={`h-8 w-full text-sm border border-gray-200 rounded-md px-3 bg-white
                        focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                        ${prefix ? 'pl-6' : ''} ${suffix ? 'pr-10' : ''}`}
                />
                {suffix && (
                    <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-gray-400 pointer-events-none">
                        {suffix}
                    </span>
                )}
            </div>
        </div>
    );
}

function ParamSection({ title, open, onToggle, children }) {
    return (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
            <button
                onClick={onToggle}
                className="w-full flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors"
            >
                <div className="flex items-center gap-3">
                    <span className="text-sm font-semibold text-gray-900">{title}</span>
                    {!open && (
                        <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                            Click to edit
                        </span>
                    )}
                </div>
                {open
                    ? <ChevronUp className="w-4 h-4 text-gray-400" />
                    : <ChevronDown className="w-4 h-4 text-gray-400" />}
            </button>
            {open && (
                <div className="px-6 pb-6 border-t border-gray-50">
                    {children}
                </div>
            )}
        </div>
    );
}

function StepHeader({ n, label, right }) {
    return (
        <div className="flex items-center gap-3 mb-4">
            <div className="w-7 h-7 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center shrink-0">
                {n}
            </div>
            <h2 className="text-base font-semibold text-gray-900">{label}</h2>
            {right && <div className="ml-auto">{right}</div>}
        </div>
    );
}

function ParamGroup({ label, color, children }) {
    return (
        <div>
            <div className="flex items-center gap-2 mb-4">
                <div className={`w-2 h-4 rounded-full ${color === 'blue' ? 'bg-blue-500' : 'bg-gray-400'}`} />
                <span className={`text-xs font-semibold uppercase tracking-wide ${
                    color === 'blue' ? 'text-blue-600' : 'text-gray-500'
                }`}>
                    {label}
                </span>
            </div>
            <div className="grid grid-cols-2 gap-3">{children}</div>
        </div>
    );
}

/* ── Floating TCO widget ── */

function FloatingTCO({ evTCO, dieselTCO, evLabel, dieselLabel }) {
    const [collapsed, setCollapsed] = useState(false);

    const diff = evTCO.total - dieselTCO.total;
    const evWins = diff < 0;
    const fmt = v => '$' + Math.round(Math.abs(v)).toLocaleString();

    return (
        <div className="fixed right-5 top-1/2 -translate-y-1/2 z-40 hidden lg:block">
            <AnimatePresence mode="wait">
                {collapsed ? (
                    /* ── Minimized pill ── */
                    <motion.button
                        key="pill"
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                        transition={{ duration: 0.2 }}
                        onClick={() => setCollapsed(false)}
                        className="flex items-center gap-2 bg-white/95 backdrop-blur-md border border-gray-200 shadow-lg
                            rounded-full pl-3 pr-3.5 py-2.5 hover:shadow-xl hover:border-gray-300 transition-all group"
                        title="Show Live TCO"
                    >
                        <PanelRightOpen className="w-3.5 h-3.5 text-gray-400 group-hover:text-gray-600 transition-colors" />
                        <div className="flex flex-col items-end leading-none gap-1">
                            <span className="text-xs font-bold text-blue-600">{fmt(evTCO.total)}</span>
                            <span className="text-xs font-bold text-gray-500">{fmt(dieselTCO.total)}</span>
                        </div>
                    </motion.button>
                ) : (
                    /* ── Expanded panel ── */
                    <motion.div
                        key="panel"
                        initial={{ opacity: 0, x: 40 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 40 }}
                        transition={{ duration: 0.25 }}
                    >
                        <div className="w-56 bg-white/95 backdrop-blur-md rounded-2xl border border-gray-200 shadow-xl overflow-hidden">
                            {/* Header */}
                            <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50 border-b border-gray-100">
                                <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Live TCO</span>
                                <button
                                    onClick={() => setCollapsed(true)}
                                    className="text-gray-300 hover:text-gray-500 transition-colors"
                                    title="Minimize"
                                >
                                    <PanelRightClose className="w-3.5 h-3.5" />
                                </button>
                            </div>

                            {/* EV row */}
                            <div className="px-4 py-3 border-b border-gray-50">
                                <div className="flex items-center gap-2 mb-1">
                                    <Zap className="w-3 h-3 text-blue-500" />
                                    <span className="text-[10px] text-gray-400 truncate">{evLabel}</span>
                                </div>
                                <div className="text-lg font-bold text-blue-700 leading-tight">{fmt(evTCO.total)}</div>
                            </div>

                            {/* Diesel row */}
                            <div className="px-4 py-3 border-b border-gray-50">
                                <div className="flex items-center gap-2 mb-1">
                                    <Fuel className="w-3 h-3 text-gray-400" />
                                    <span className="text-[10px] text-gray-400 truncate">{dieselLabel}</span>
                                </div>
                                <div className="text-lg font-bold text-gray-700 leading-tight">{fmt(dieselTCO.total)}</div>
                            </div>

                            {/* Diff row */}
                            <div className={`px-4 py-3 flex items-center gap-2 ${evWins ? 'bg-green-50' : 'bg-amber-50'}`}>
                                {evWins
                                    ? <TrendingDown className="w-3.5 h-3.5 text-green-500 shrink-0" />
                                    : <TrendingUp className="w-3.5 h-3.5 text-amber-500 shrink-0" />}
                                <div>
                                    <div className={`text-sm font-bold leading-tight ${evWins ? 'text-green-600' : 'text-amber-600'}`}>
                                        {fmt(diff)} {evWins ? 'saved' : 'more'}
                                    </div>
                                    <div className="text-[10px] text-gray-400">
                                        {evWins ? 'EV advantage' : 'Diesel/Gas advantage'}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

/* ── Main component ── */

export default function BasicCalculator() {
    const [evClassFilter, setEvClassFilter] = useState(DEFAULT_CLASS_FILTER);
    const [iceClassFilter, setIceClassFilter] = useState(DEFAULT_CLASS_FILTER);
    const [evMake, setEvMake] = useState(DEFAULT_EV_SELECTION.make);
    const [evModel, setEvModel] = useState(DEFAULT_EV_SELECTION.model);
    const [dieselMake, setDieselMake] = useState(DEFAULT_ICE_SELECTION.make);
    const [dieselModel, setDieselModel] = useState(DEFAULT_ICE_SELECTION.model);

    const [evFin, setEvFin] = useState({ ...DEFAULT_EV_FIN });
    const [dieselFin, setDieselFin] = useState({ ...DEFAULT_DIESEL_FIN });
    const [evOps, setEvOps] = useState({ ...DEFAULT_EV_OPS });
    const [dieselOps, setDieselOps] = useState({ ...DEFAULT_DIESEL_OPS });

    const [finOpen, setFinOpen] = useState(false);
    const [opsOpen, setOpsOpen] = useState(false);

    const [ownChargers, setOwnChargers] = useState('yes');
    const [selectedChargerType, setSelectedChargerType] = useState(ChargerOptions[0].power.toString());
    const [incentiveModalOpen, setIncentiveModalOpen] = useState(false);
    const [hasTrackedFirstAdjustment, setHasTrackedFirstAdjustment] = useState(false);

    const evFilteredEntries = useMemo(
        () => filterEntriesByClass(EV_ENTRIES, evClassFilter),
        [evClassFilter],
    );
    const iceFilteredEntries = useMemo(
        () => filterEntriesByClass(ICE_STANDARD_ENTRIES, iceClassFilter),
        [iceClassFilter],
    );
    const evIndex = useMemo(
        () => buildSelectionIndex(evFilteredEntries, formatEvModelLabel),
        [evFilteredEntries],
    );
    const iceIndex = useMemo(() => buildSelectionIndex(iceFilteredEntries), [iceFilteredEntries]);
    const isOwn = ownChargers === 'yes';

    const evTCO = useMemo(
        () => calculateTCO(evFin, evOps, true, isOwn),
        [evFin, evOps, isOwn],
    );
    const dieselTCO = useMemo(
        () => calculateTCO(dieselFin, dieselOps, false),
        [dieselFin, dieselOps],
    );
    const diff = evTCO.total - dieselTCO.total;
    const evWins = diff < 0;

    useEffect(() => {
        trackMarketingFunnel('basic_calculator_page_view');
    }, []);

    useEffect(() => {
        if (hasTrackedFirstAdjustment) return;

        const changedFromDefaults =
            evClassFilter !== DEFAULT_CLASS_FILTER ||
            iceClassFilter !== DEFAULT_CLASS_FILTER ||
            evMake !== DEFAULT_EV_SELECTION.make ||
            evModel !== DEFAULT_EV_SELECTION.model ||
            dieselMake !== DEFAULT_ICE_SELECTION.make ||
            dieselModel !== DEFAULT_ICE_SELECTION.model ||
            ownChargers !== 'yes' ||
            selectedChargerType !== ChargerOptions[0].power.toString() ||
            JSON.stringify(evFin) !== JSON.stringify(DEFAULT_EV_FIN) ||
            JSON.stringify(dieselFin) !== JSON.stringify(DEFAULT_DIESEL_FIN) ||
            JSON.stringify(evOps) !== JSON.stringify(DEFAULT_EV_OPS) ||
            JSON.stringify(dieselOps) !== JSON.stringify(DEFAULT_DIESEL_OPS);

        if (changedFromDefaults) {
            setHasTrackedFirstAdjustment(true);
            trackMarketingFunnel('basic_calculator_first_adjustment');
        }
    }, [
        hasTrackedFirstAdjustment,
        evClassFilter,
        iceClassFilter,
        evMake,
        evModel,
        dieselMake,
        dieselModel,
        ownChargers,
        selectedChargerType,
        evFin,
        dieselFin,
        evOps,
        dieselOps,
    ]);

    const handleReset = () => {
        setEvFin({ ...DEFAULT_EV_FIN });
        setDieselFin({ ...DEFAULT_DIESEL_FIN });
        setEvOps({ ...DEFAULT_EV_OPS });
        setDieselOps({ ...DEFAULT_DIESEL_OPS });
        setEvClassFilter(DEFAULT_CLASS_FILTER);
        setIceClassFilter(DEFAULT_CLASS_FILTER);
        setEvMake(DEFAULT_EV_SELECTION.make);
        setEvModel(DEFAULT_EV_SELECTION.model);
        setDieselMake(DEFAULT_ICE_SELECTION.make);
        setDieselModel(DEFAULT_ICE_SELECTION.model);
        setOwnChargers('yes');
        setSelectedChargerType(ChargerOptions[0].power.toString());
    };

    const upEF = (k, v) => setEvFin(p => ({ ...p, [k]: v }));
    const upDF = (k, v) => setDieselFin(p => ({ ...p, [k]: v }));
    const upEO = (k, v) => setEvOps(p => ({ ...p, [k]: v }));
    const upDO = (k, v) => setDieselOps(p => ({ ...p, [k]: v }));

    const handleEvMakeChange = (make) => {
        setEvMake(make);
        const firstModel = evIndex.data[make]?.models?.[0] || '';
        setEvModel(firstModel);
        const selectedEntry = getEntryFromIndex(evIndex, make, firstModel);
        if (selectedEntry?.price) upEF('msrp', selectedEntry.price);
        upEF('incentives', selectedEntry?.incentives || 0);
        if (selectedEntry?.energyEfficiency) upEO('energyEfficiency', selectedEntry.energyEfficiency);
    };

    const handleEvModelChange = (model) => {
        setEvModel(model);
        const selectedEntry = getEntryFromIndex(evIndex, evMake, model);
        if (selectedEntry?.price) upEF('msrp', selectedEntry.price);
        upEF('incentives', selectedEntry?.incentives || 0);
        if (selectedEntry?.energyEfficiency) upEO('energyEfficiency', selectedEntry.energyEfficiency);
    };

    const handleDieselMakeChange = (make) => {
        setDieselMake(make);
        const firstModel = iceIndex.data[make]?.models?.[0] || '';
        setDieselModel(firstModel);
        const selectedEntry = getEntryFromIndex(iceIndex, make, firstModel);
        if (selectedEntry?.price) upDF('msrp', selectedEntry.price);
        if (selectedEntry?.fuelEconomy) upDO('fuelEfficiency', selectedEntry.fuelEconomy);
    };

    const handleDieselModelChange = (model) => {
        setDieselModel(model);
        const selectedEntry = getEntryFromIndex(iceIndex, dieselMake, model);
        if (selectedEntry?.price) upDF('msrp', selectedEntry.price);
        if (selectedEntry?.fuelEconomy) upDO('fuelEfficiency', selectedEntry.fuelEconomy);
    };

    const handleEvClassFilterChange = (vehicleClass) => {
        setEvClassFilter(vehicleClass);
        const nextEntries = filterEntriesByClass(EV_ENTRIES, vehicleClass);
        const nextIndex = buildSelectionIndex(nextEntries, formatEvModelLabel);
        const nextSelection = getDefaultSelection(nextEntries, nextIndex);
        setEvMake(nextSelection.make);
        setEvModel(nextSelection.model);
        const selectedEntry = getEntryFromIndex(nextIndex, nextSelection.make, nextSelection.model);
        if (selectedEntry?.price) upEF('msrp', selectedEntry.price);
        upEF('incentives', selectedEntry?.incentives || 0);
        if (selectedEntry?.energyEfficiency) upEO('energyEfficiency', selectedEntry.energyEfficiency);
    };

    const handleIceClassFilterChange = (vehicleClass) => {
        setIceClassFilter(vehicleClass);
        const nextEntries = filterEntriesByClass(ICE_STANDARD_ENTRIES, vehicleClass);
        const nextIndex = buildSelectionIndex(nextEntries);
        const nextSelection = getDefaultSelection(nextEntries, nextIndex);
        setDieselMake(nextSelection.make);
        setDieselModel(nextSelection.model);
        const selectedEntry = getEntryFromIndex(nextIndex, nextSelection.make, nextSelection.model);
        if (selectedEntry?.price) upDF('msrp', selectedEntry.price);
        if (selectedEntry?.fuelEconomy) upDO('fuelEfficiency', selectedEntry.fuelEconomy);
    };

    const handleChargerTypeChange = (power) => {
        setSelectedChargerType(power);
        const charger = ChargerOptions.find(c => c.power.toString() === power);
        if (charger) {
            setEvOps(p => ({ ...p, chargerCost: charger.price, peakLoad: charger.power }));
        }
    };

    return (
        <div className="bg-gray-50">
            {/* Hero */}
            <section
                style={{ background: 'linear-gradient(135deg, #1e3a8a 0%, #2563eb 65%, #3b82f6 100%)' }}
                className="mt-16 py-16 px-6"
            >
                <div className="max-w-6xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 15 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                    >
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/15 rounded-full text-xs text-blue-100 font-medium mb-5">
                            <div className="w-1.5 h-1.5 rounded-full bg-blue-300" />
                            Free baseline tool powered by eFleetPlan
                        </div>
                        <h1 className="text-4xl md:text-5xl font-bold text-white mb-3">
                            Quick EV vs Diesel/Gas Baseline Estimate
                        </h1>
                        <p className="text-blue-200 max-w-lg text-sm leading-relaxed">
                            Start with a fast single-vehicle estimate to understand baseline economics.
                            For route-level operations, charging strategy, and multi-depot planning, continue in eFleetPlan.
                        </p>
                        <div className="mt-6 flex flex-col sm:flex-row gap-3">
                            <Link
                                to="/subscription?module=fleet"
                                onClick={() => trackMarketingFunnel('basic_calculator_hero_upgrade_cta_click')}
                                className="inline-flex items-center justify-center rounded-lg bg-white text-blue-700 px-5 py-2.5 text-sm font-semibold hover:bg-blue-50 transition-colors"
                            >
                                Continue in eFleetPlan
                            </Link>
                            <Link
                                to="/solutions/fleet"
                                onClick={() => trackMarketingFunnel('basic_calculator_hero_learn_more_click')}
                                className="inline-flex items-center justify-center rounded-lg border border-white/50 text-white px-5 py-2.5 text-sm font-semibold hover:bg-white/10 transition-colors"
                            >
                                Learn what fleet-scale includes
                            </Link>
                        </div>
                    </motion.div>
                </div>
            </section>

            <main className="max-w-6xl mx-auto px-6 py-10 space-y-8">
                {/* ─── Step 1 ─── */}
                <div>
                    <StepHeader
                        n="1"
                        label="Select Vehicles"
                        right={
                            <button
                                onClick={handleReset}
                                className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1 transition-colors"
                            >
                                <RotateCcw className="w-3 h-3" /> Reset
                            </button>
                        }
                    />
                    <div className="grid md:grid-cols-2 gap-5">
                        <VehicleCard
                            type="ev"
                            data={evIndex.data}
                            selectedMake={evMake}
                            selectedModel={evModel}
                            selectedClass={evClassFilter}
                            classOptions={VEHICLE_CLASS_OPTIONS}
                            onClassChange={handleEvClassFilterChange}
                            onMakeChange={handleEvMakeChange}
                            onModelChange={handleEvModelChange}
                        />
                        <VehicleCard
                            type="diesel"
                            data={iceIndex.data}
                            selectedMake={dieselMake}
                            selectedModel={dieselModel}
                            selectedClass={iceClassFilter}
                            classOptions={VEHICLE_CLASS_OPTIONS}
                            onClassChange={handleIceClassFilterChange}
                            disableMakeModel={true}
                            forceDefaultSelectors={true}
                            onMakeChange={handleDieselMakeChange}
                            onModelChange={handleDieselModelChange}
                        />
                    </div>
                    <div className="mt-3 text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 leading-relaxed">
                        EV model options above include only California
                        {' '}
                        <a
                            href="https://californiahvip.org/vehicles/"
                            target="_blank"
                            rel="noreferrer"
                            className="text-blue-600 hover:text-blue-700 underline"
                        >
                            HVIP-listed vehicles
                        </a>
                        . MSRPs are estimated based on public information. Range values are estimated from battery capacity data using class-based energy consumption assumptions. If your target EV model is not listed, you can ignore model selection and adjust MSRP, incentives, efficiency, and other inputs directly.
                    </div>
                </div>

                {/* ─── Step 2 ─── */}
                <div>
                    <StepHeader n="2" label="Configure Parameters" />
                    <div className="space-y-3">
                        {/* Financial Parameters */}
                        <ParamSection title="Financial Parameters" open={finOpen} onToggle={() => setFinOpen(!finOpen)}>
                            <div className="grid md:grid-cols-2 gap-8 mt-5">
                                {/* EV Financial */}
                                <ParamGroup label="Electric Vehicle" color="blue">
                                    <ParamField label="Vehicle MSRP" value={evFin.msrp} onChange={v => upEF('msrp', v)} prefix="$" step="1000" />
                                    <ParamField
                                        label="Incentives"
                                        value={evFin.incentives}
                                        onChange={v => upEF('incentives', v)}
                                        prefix="$"
                                        step="1000"
                                        labelRight={
                                            <button
                                                type="button"
                                                onClick={() => setIncentiveModalOpen(true)}
                                                className="text-[10px] font-medium text-blue-500 hover:text-blue-700 transition-colors"
                                            >
                                                Incentive Calculator &rarr;
                                            </button>
                                        }
                                    />
                                    <ParamField label="Tax Rate" value={evFin.taxRate} onChange={v => upEF('taxRate', v)} suffix="%" step="0.1" />
                                    <ParamField label="Down Payment" value={evFin.downPaymentPct} onChange={v => upEF('downPaymentPct', v)} suffix="%" step="1" />
                                    <ParamField label="Loan Period" value={evFin.loanPeriod} onChange={v => upEF('loanPeriod', v)} suffix="yr" step="1" />
                                    <ParamField label="Interest Rate" value={evFin.interestRate} onChange={v => upEF('interestRate', v)} suffix="%" step="0.1" />
                                    <ParamField label="Holding Period" value={evFin.holdingPeriod} onChange={v => upEF('holdingPeriod', v)} suffix="yr" step="1" />
                                    <ParamField label="Residual Value" value={evFin.residualValue} onChange={v => upEF('residualValue', v)} suffix="%" step="1" />
                                    <ParamField label="Annual Insurance" value={evFin.insurance} onChange={v => upEF('insurance', v)} prefix="$" step="500" />
                                    <ParamField label="Registration Fee" value={evFin.registrationFee} onChange={v => upEF('registrationFee', v)} prefix="$" step="100" />
                                </ParamGroup>

                                {/* Diesel/Gas Financial */}
                                <ParamGroup label="Diesel/Gas Vehicle" color="gray">
                                    <ParamField label="Vehicle MSRP" value={dieselFin.msrp} onChange={v => upDF('msrp', v)} prefix="$" step="1000" />
                                    <ParamField label="Incentives" value={dieselFin.incentives} onChange={v => upDF('incentives', v)} prefix="$" step="1000" />
                                    <ParamField label="Tax Rate" value={dieselFin.taxRate} onChange={v => upDF('taxRate', v)} suffix="%" step="0.1" />
                                    <ParamField label="Down Payment" value={dieselFin.downPaymentPct} onChange={v => upDF('downPaymentPct', v)} suffix="%" step="1" />
                                    <ParamField label="Loan Period" value={dieselFin.loanPeriod} onChange={v => upDF('loanPeriod', v)} suffix="yr" step="1" />
                                    <ParamField label="Interest Rate" value={dieselFin.interestRate} onChange={v => upDF('interestRate', v)} suffix="%" step="0.1" />
                                    <ParamField label="Holding Period" value={dieselFin.holdingPeriod} onChange={v => upDF('holdingPeriod', v)} suffix="yr" step="1" />
                                    <ParamField label="Residual Value" value={dieselFin.residualValue} onChange={v => upDF('residualValue', v)} suffix="%" step="1" />
                                    <ParamField label="Annual Insurance" value={dieselFin.insurance} onChange={v => upDF('insurance', v)} prefix="$" step="500" />
                                    <ParamField label="Registration Fee" value={dieselFin.registrationFee} onChange={v => upDF('registrationFee', v)} prefix="$" step="100" />
                                </ParamGroup>
                            </div>
                        </ParamSection>

                        {/* Operational Parameters */}
                        <ParamSection title="Operational Parameters" open={opsOpen} onToggle={() => setOpsOpen(!opsOpen)}>
                            <div className="grid md:grid-cols-2 gap-8 mt-5">
                                {/* EV Operational */}
                                <div className="space-y-6">
                                    <div>
                                        <div className="flex items-center gap-2 mb-4">
                                            <div className="w-2 h-4 rounded-full bg-blue-500" />
                                            <span className="text-xs font-semibold uppercase tracking-wide text-blue-600">Electric Vehicle</span>
                                        </div>
                                        <div className="space-y-3">
                                            <ParamField label="Annual Mileage" value={evOps.annualMileage} onChange={v => upEO('annualMileage', v)} step="1000" />
                                            <div className="grid grid-cols-2 gap-3">
                                                <ParamField label="Maintenance" value={evOps.maintenanceCost} onChange={v => upEO('maintenanceCost', v)} prefix="$" suffix="/mi" step="0.001" />
                                                <ParamField label="Tire Cost" value={evOps.tireCost} onChange={v => upEO('tireCost', v)} prefix="$" suffix="/mi" step="0.001" />
                                            </div>
                                            <div className="grid grid-cols-2 gap-3">
                                                <ParamField label="Energy Efficiency" value={evOps.energyEfficiency} onChange={v => upEO('energyEfficiency', v)} suffix="kWh/mi" step="0.1" />
                                                <ParamField label="Electricity Cost" value={evOps.electricityCost} onChange={v => upEO('electricityCost', v)} prefix="$" suffix="/kWh" step="0.01" />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Charging section */}
                                    <div className="border-t border-gray-100 pt-5">
                                        <div className="flex items-center gap-2 mb-4">
                                            <div className="w-2 h-4 rounded-full bg-blue-500" />
                                            <span className="text-xs font-semibold uppercase tracking-wide text-blue-600">
                                                Charging
                                            </span>
                                        </div>

                                        {/* Own vs commercial toggle */}
                                        <div className="bg-blue-50/60 rounded-xl p-4 mb-4 space-y-2.5">
                                            <p className="text-xs font-medium text-gray-600 mb-2">How do you plan to charge?</p>
                                            <label className="flex items-center gap-2.5 cursor-pointer">
                                                <input
                                                    type="radio"
                                                    name="charger-option"
                                                    value="yes"
                                                    checked={isOwn}
                                                    onChange={() => setOwnChargers('yes')}
                                                    className="w-3.5 h-3.5 text-blue-600 border-gray-300 focus:ring-blue-500"
                                                />
                                                <span className="text-xs text-gray-700">I plan to own my chargers</span>
                                            </label>
                                            <label className="flex items-center gap-2.5 cursor-pointer">
                                                <input
                                                    type="radio"
                                                    name="charger-option"
                                                    value="no"
                                                    checked={!isOwn}
                                                    onChange={() => setOwnChargers('no')}
                                                    className="w-3.5 h-3.5 text-blue-600 border-gray-300 focus:ring-blue-500"
                                                />
                                                <span className="text-xs text-gray-700">I plan to use commercial charging stations</span>
                                            </label>
                                        </div>

                                        {/* Charger type selector (only when owning) */}
                                        {isOwn && (
                                            <div className="mb-4">
                                                <label className="text-xs text-gray-400 mb-1 block">Charger Type</label>
                                                <select
                                                    value={selectedChargerType}
                                                    onChange={e => handleChargerTypeChange(e.target.value)}
                                                    className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white text-gray-900
                                                        focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition-colors"
                                                >
                                                    {ChargerOptions.map(opt => (
                                                        <option key={opt.power} value={opt.power.toString()}>
                                                            {opt.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        )}

                                        {/* Conditional charging fields */}
                                        <div className="grid grid-cols-2 gap-3">
                                            {isOwn ? (
                                                <>
                                                    <ParamField label="Charger Cost" value={evOps.chargerCost} onChange={v => upEO('chargerCost', v)} prefix="$" step="1000" />
                                                    <ParamField label="Charger Incentives" value={evOps.chargerIncentives} onChange={v => upEO('chargerIncentives', v)} prefix="$" step="1000" />
                                                    <ParamField label="Demand Charge" value={evOps.demandCharge} onChange={v => upEO('demandCharge', v)} prefix="$" suffix="/kW" step="1" />
                                                    <ParamField label="Peak Load" value={evOps.peakLoad} onChange={v => upEO('peakLoad', v)} suffix="kW" step="50" />
                                                </>
                                            ) : (
                                                <ParamField label="Charging Fee" value={evOps.thirdPartyFee} onChange={v => upEO('thirdPartyFee', v)} prefix="$" suffix="/kWh" step="0.01" />
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Diesel/Gas Operational */}
                                <div>
                                    <div className="flex items-center gap-2 mb-4">
                                        <div className="w-2 h-4 rounded-full bg-gray-400" />
                                        <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">Diesel/Gas Vehicle</span>
                                    </div>
                                    <div className="space-y-3">
                                        <ParamField label="Annual Mileage" value={dieselOps.annualMileage} onChange={v => upDO('annualMileage', v)} step="1000" />
                                        <div className="grid grid-cols-2 gap-3">
                                            <ParamField label="Maintenance" value={dieselOps.maintenanceCost} onChange={v => upDO('maintenanceCost', v)} prefix="$" suffix="/mi" step="0.001" />
                                            <ParamField label="Tire Cost" value={dieselOps.tireCost} onChange={v => upDO('tireCost', v)} prefix="$" suffix="/mi" step="0.001" />
                                        </div>
                                        <div className="grid grid-cols-2 gap-3">
                                            <ParamField label="Fuel Efficiency" value={dieselOps.fuelEfficiency} onChange={v => upDO('fuelEfficiency', v)} suffix="MPG" step="0.5" />
                                            <ParamField label="Diesel/Gas Cost" value={dieselOps.fuelCost} onChange={v => upDO('fuelCost', v)} prefix="$" suffix="/gal" step="0.05" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </ParamSection>
                    </div>
                </div>

                {/* ─── Step 3 ─── */}
                <div>
                    <StepHeader
                        n="3"
                        label="TCO Results"
                        right={
                            <span className="text-xs text-gray-400">
                                {evFin.holdingPeriod}-year holding period
                            </span>
                        }
                    />
                    <TCOResults
                        evTCO={evTCO}
                        dieselTCO={dieselTCO}
                        evMake={evMake}
                        evModel={evModel}
                        dieselMake="Diesel/Gas"
                        dieselModel="Default"
                    />
                    <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-5">
                        <h3 className="text-sm font-semibold text-slate-900">
                            What this baseline estimate does not cover
                        </h3>
                        <p className="text-xs text-slate-600 mt-2 leading-relaxed">
                            This calculator is designed for quick single-vehicle screening. Fleet deployment decisions
                            usually require route fit, charging strategy, infrastructure phasing, and multi-vehicle scheduling analysis.
                        </p>
                        <ul className="mt-3 text-xs text-slate-700 space-y-1.5">
                            <li>- Route and duty-cycle feasibility</li>
                            <li>- Depot vs public charging operations</li>
                            <li>- Multi-depot rollout and infrastructure constraints</li>
                        </ul>
                        <div className="mt-4 flex flex-col sm:flex-row gap-3">
                            <Link
                                to="/subscription?module=fleet"
                                onClick={() => trackMarketingFunnel('basic_calculator_results_upgrade_cta_click', { outcome: evWins ? 'ev_advantage' : 'diesel_gas_advantage' })}
                                className="inline-flex items-center justify-center rounded-lg bg-blue-600 text-white px-4 py-2 text-xs font-semibold hover:bg-blue-700 transition-colors"
                            >
                                Run fleet-wide analysis in eFleetPlan
                            </Link>
                            <Link
                                to="/contact"
                                onClick={() => trackMarketingFunnel('basic_calculator_results_contact_cta_click')}
                                className="inline-flex items-center justify-center rounded-lg border border-slate-300 text-slate-700 px-4 py-2 text-xs font-semibold hover:bg-white transition-colors"
                            >
                                Book a fleet assessment
                            </Link>
                        </div>
                        <p className="text-[11px] text-slate-500 mt-3">
                            Baseline output: {evWins ? 'EV shows lower modeled TCO' : 'Diesel/Gas shows lower modeled TCO'} in this setup.
                        </p>
                    </div>
                </div>
            </main>

            {/* Floating TCO widget */}
            <FloatingTCO
                evTCO={evTCO}
                dieselTCO={dieselTCO}
                evLabel={`${evMake} ${evModel}`}
                dieselLabel="Diesel/Gas Default"
            />

            {/* Incentive Calculator Modal */}
            {incentiveModalOpen && (
                <IncentiveCalculator
                    setOpenModal={setIncentiveModalOpen}
                    evMsrpCost={evFin.msrp}
                    onChange={val => upEF('incentives', val)}
                />
            )}
        </div>
    );
}
